import { useMemo } from 'react'

import { DashCard } from './DashboardCards'
import { dragText, mandalOfficerRollup } from '../../../lib/mandal-officers'

import type { GisSite } from '../../../api/types'

const TOP_N = 5

/** Completion tone by the same bands the Officers pie uses (≥85 on-track, 60–84 watch, <60 below). */
const completionColor = (pct: number): string => (pct >= 85 ? '#0f7a52' : pct >= 60 ? '#ed8a0a' : '#e02d22')

interface OfficerPushCardProps {
  /** Sites in the current scope (mandal + entity applied) — the rollup input. */
  sites: GisSite[]
  pending: boolean
  /** Any click → the Coverage board's Officers view (worst completion leads it). */
  onOpen: () => void
}

/**
 * "Officers needing a push" — the worst-completion officers over the scoped sites, with exactly
 * what's dragging them (never/overdue/in-progress counts). The same rollup the Coverage board's
 * Officers view leads with, so clicking through lands on the identical ranking in full.
 */
export function OfficerPushCard({ sites, pending, onOpen }: OfficerPushCardProps) {
  const rows = useMemo(() => mandalOfficerRollup(sites).slice(0, TOP_N), [sites])

  return (
    <DashCard title="Officers needing a push" caption="Worst completion first" onOpen={onOpen} openLabel="Open Coverage board — Officers view">
      {pending ? (
        <div className="flex flex-col gap-2 py-1" aria-hidden>
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-[26px] animate-pulse rounded-[8px] bg-gis-track-2" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="py-4 text-center text-[11.5px] text-gis-soft">No sites in this selection.</div>
      ) : (
        <div className="flex flex-col gap-[6px]">
          {rows.map((r) => (
            <button
              key={r.officerId ?? 'unassigned'}
              type="button"
              onClick={onOpen}
              title="Open the Coverage board's Officers view"
              className="-mx-1 flex cursor-pointer items-center gap-3 rounded-[8px] px-1 py-[3px] text-left transition-colors hover:bg-[#f5f7fa] focus-visible:ring-2 focus-visible:ring-blue-500/60"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[12px] font-semibold text-gis-ink-2">
                  {r.name}
                  {r.thin && <span className="ml-1 align-middle text-[9px] font-medium text-gis-muted">(thin data)</span>}
                </span>
                <span className="block truncate text-[10px] text-gis-soft">
                  {r.assigned} site{r.assigned === 1 ? '' : 's'} · {dragText(r)}
                </span>
              </span>
              <span className="flex flex-none items-center gap-2">
                <span className="h-[6px] w-16 overflow-hidden rounded-full bg-gis-track-2">
                  <span
                    className="block h-full rounded-full transition-[width] duration-[450ms] ease-[cubic-bezier(.2,.7,.3,1)]"
                    style={{ width: `${r.completionPct}%`, background: completionColor(r.completionPct) }}
                  />
                </span>
                <span
                  className="min-w-[34px] text-right text-[11.5px] font-bold tabular-nums"
                  style={{ color: completionColor(r.completionPct) }}
                >
                  {r.completionPct}%
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </DashCard>
  )
}
